import React, { useState } from 'react';
import logo from '../images/icon_small.png';
import { imageSrc, isAnimatedImage } from '../utils/avatar';
import { useLayoutMode } from '../hooks/useLayoutMode';
import { useProfile } from '../profile/ProfileContext';

const reducedMotion = () => typeof window !== 'undefined' && window.matchMedia
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Every user avatar / group icon in the app. Renders a single <img>, with
// whatever className the caller's CSS already targets - so swapping a plain
// <img> for this changes nothing on screen.
//
// - Animated images show their first frame; they play while hovered (desktop)
//   or always with animate="always" (an opened profile). Never on phones in
//   lists, never with reduced motion.
// - username: clicking opens that person's profile card (seed = what's
//   already known, shown while it loads).
// - zoom: clicking opens the image itself, full size.
export default function Avatar({
  imageUrl, size = 64, alt = '', className = '', fallback = logo, animate = 'hover',
  username, seed, zoom = false, onClick, ...rest
}) {
  const { openProfile, openImage } = useProfile();
  const { isMobile } = useLayoutMode();
  const [hover, setHover] = useState(false);
  const animated = isAnimatedImage(imageUrl);
  const play = animated && !reducedMotion() && (animate === 'always' || (animate === 'hover' && hover && !isMobile));
  const src = imageSrc(imageUrl, { size, animated: play }) || fallback;

  const interactive = !!(onClick || username || (zoom && imageUrl));
  const activate = (e) => {
    if (onClick) return onClick(e);
    // The avatar often sits inside a clickable row (open chat, select
    // friend) - opening the profile shouldn't also do that.
    e.stopPropagation();
    if (username) openProfile(username, e.currentTarget, seed);
    else if (zoom) openImage(imageUrl, alt);
    return undefined;
  };

  return (
    <img
      src={src}
      alt={alt}
      className={interactive ? `${className} AvatarClickable`.trim() : (className || undefined)}
      onMouseEnter={animated ? () => setHover(true) : undefined}
      onMouseLeave={animated ? () => setHover(false) : undefined}
      onClick={interactive ? activate : undefined}
      onKeyDown={interactive ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); activate(e); } } : undefined}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      {...rest}
    />
  );
}
